/**
 * Cadastro, edição e consulta de instalação com inflamáveis em etapas
 * (rotas /flammable-installations/new e /flammable-installations/:id) — issue #366.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao } from '../../authorization/useVeredito';
import { departmentService } from '../../services/departmentService';
import { flammableInstallationService } from '../../services/flammableInstallationService';
import type { Department } from '../../types/department';
import type { FlammableInstallation } from '../../types/flammableInstallation';
import { FlammableInstallationWizard } from './components/FlammableInstallationWizard';
import {
  EMPTY_IDENTIFICATION,
  type IdentificationValues,
} from './components/FlammableInstallationWizard/FlammableInstallationWizard.types';
import { mensagemDoServidor } from './components/mensagensDeErro';

const LIST_PATH = '/flammable-installations';

/** Os setores do cadastro; só são pedidos a quem pode ler setores (`useListaPermitida`). */
const loadDepartments = async (companyId: string): Promise<readonly Department[]> =>
  (await departmentService.getDepartments({ limit: 200, ...(companyId === '' ? {} : { companyId }) })).data;

const valuesFrom = (installation: FlammableInstallation): IdentificationValues => ({
  companyId: String(installation.companyId),
  departmentId: installation.departmentId === null ? '' : String(installation.departmentId),
  name: installation.name,
  tag: installation.tag ?? '',
  activityType: installation.activityType,
  activityDescription: installation.activityDescription ?? '',
  location: installation.location ?? '',
  status: installation.status,
  observations: installation.observations ?? '',
  classification: installation.summary?.classification ?? null,
});

const FlammableInstallationWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const create = useAcao('flammable_installation.create', 'Cadastrar instalação');
  const update = useAcao('flammable_installation.update', 'Editar instalação');
  const canUpdate = !update.disabled;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<IdentificationValues>(EMPTY_IDENTIFICATION);
  const [installationName, setInstallationName] = useState<string | null>(null);

  // Abrir uma instalação existente só pede `.read` (a porta da rota); criar pede `.create`.
  const allowed = id ? true : !create.disabled;
  // Com empresa escolhida na etapa 1, só os setores dela são buscados; sem escolha, vale a lista toda.
  const [companyId, setCompanyId] = useState('');
  const loadCompanyDepartments = useCallback(() => loadDepartments(companyId), [companyId]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadCompanyDepartments, {
    habilitado: allowed,
  });
  const departments = useMemo(() => [...departmentList.itens], [departmentList.itens]);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }
    let active = true;

    const load = async () => {
      try {
        const installation = id ? await flammableInstallationService.getInstallationById(id) : null;
        if (!active) return;
        if (installation) {
          setInitialValues(valuesFrom(installation));
          setInstallationName(installation.name);
        }
      } catch (err: unknown) {
        if (active) setError(mensagemDoServidor(err, 'Não foi possível abrir a instalação.'));
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [id, allowed]);

  const readOnly = Boolean(id) && !canUpdate;
  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Instalações com inflamáveis', path: LIST_PATH },
    { label: id ? (installationName ?? 'Instalação com inflamáveis') : 'Nova instalação' },
  ];
  const title = !id ? 'Cadastrar instalação com inflamáveis' : readOnly ? 'Consultar instalação com inflamáveis' : 'Editar instalação com inflamáveis';

  if (!allowed) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="info"
          sx={{ fontSize: '1rem' }}
          action={
            <Button color="inherit" onClick={() => navigate(LIST_PATH)} sx={{ minHeight: 44 }}>
              Voltar para as instalações
            </Button>
          }
        >
          Seu acesso permite ver as instalações com inflamáveis, mas não cadastrar novas. Peça a quem administra o seu
          acesso.
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={readOnly ? undefined : 'Cadastro em etapas: você pode parar e continuar depois.'}
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Carregando" />
        </Box>
      )}
      {!loading && error && (
        <Alert severity="error" sx={{ fontSize: '1rem' }}>
          {error}
        </Alert>
      )}
      {!loading && !error && (
        <FlammableInstallationWizard
          installationId={id}
          initialValues={initialValues}
          departments={departments}
          canUpdate={canUpdate}
          onCompanyChange={setCompanyId}
          onFinish={() => navigate(LIST_PATH)}
          onCancel={() => navigate(LIST_PATH)}
          onCreatedReadOnly={(createdId) => navigate(`${LIST_PATH}/${createdId}`, { replace: true })}
        />
      )}
    </PageShell>
  );
};

export default FlammableInstallationWizardPage;
